"use client";

import { Suspense, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useUser } from "@/features/auth/hooks/useUser";
import { isAdminEmail } from "@/lib/auth/admin";
import { isPaidPlan } from "@/lib/auth/plans";
import {
  identifyUser,
  isAnalyticsEnabled,
  loadAnalytics,
  resetUser,
} from "@/lib/analytics/posthog";
import { rearmLockImpressions } from "@/lib/analytics/useLockImpression";

/**
 * Mounts analytics. Renders nothing.
 *
 * Two jobs, both deliberately deferred past the first paint:
 *
 * 1. Load + init posthog-js on the first idle callback. The library is a
 *    separate async chunk (see `lib/analytics/posthog.ts`), so it competes
 *    with nothing during LCP; the 3 s timeout caps how long a busy main
 *    thread can delay the session from being recorded at all.
 * 2. Attach identity once `useUser` settles — the session AND the plan — and
 *    drop it on sign-out.
 *
 * It lives inside `AuthProviders` (so `useUser` is available) and does all its
 * work in effects — no `Date.now()`, no `headers()`, nothing that would touch
 * the prerender of the static home tree.
 */
export function AnalyticsProvider() {
  const { user, plan, subscriptionStatus, loading, serverEmail } = useUser();

  useEffect(() => {
    if (!isAnalyticsEnabled()) return;

    const idle = window.requestIdleCallback;
    if (typeof idle === "function") {
      const handle = idle(() => void loadAnalytics(), { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }

    // Safari < 16.4 and friends: a plain timer, after the page has settled.
    const timer = window.setTimeout(() => void loadAnalytics(), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const userId = user?.id ?? null;
  const email = user?.email || serverEmail || null;

  useEffect(() => {
    // `loading` is still true before the first auth resolution — identifying
    // there would race a sign-in and reset it a moment later.
    if (loading) return;
    // A signed-in user reads plan "anonymous" until `fetchPlan` resolves (it
    // runs after `loading` is already false). Identifying then stamped
    // `plan: anonymous` on the person for every fresh page load, corrected a
    // moment later — and every event in between carried the wrong plan.
    if (userId && plan === "anonymous") return;

    if (userId) {
      identifyUser({
        id: userId,
        email,
        plan,
        subscriptionStatus,
        isTeam: isAdminEmail(email),
        // Same rule as the account page's "complimentary": a paid plan with no
        // subscription behind it. Every admin is comped Studio and would
        // otherwise read as a paying customer.
        isComped: isPaidPlan(plan) && subscriptionStatus === null,
      });
    } else {
      resetUser();
    }
  }, [loading, userId, email, plan, subscriptionStatus]);

  return (
    // usePathname() outside Suspense blocks the prerender of every dynamic
    // route under this layout (/app/[id] failed the build); inside, the hole
    // renders nothing and the hook resolves after hydration.
    <Suspense fallback={null}>
      <LockImpressionRearm />
    </Suspense>
  );
}

/**
 * Each client-side page view re-arms "lock viewed" (once per surface per page
 * VIEW, not per document). Runs after the new page's locks attached; their
 * impressions only land once they're half in view, later still.
 */
function LockImpressionRearm() {
  const pathname = usePathname();
  useEffect(() => {
    rearmLockImpressions();
  }, [pathname]);
  return null;
}
